import { LocateFixed, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';


import type { Affiliation, Profile } from '@/types/profile';

import DashboardHeader from '@/components/dashboard/dashboard-header';
import AffiliationModal from '@/components/profile/affiliation-modal';
import AffiliationRemovalDialog, { type AffiliationUsage } from '@/components/profile/affiliation-removal-dialog';
import AvatarModal from '@/components/profile/avatar-modal';
import CapabilitiesSection from '@/components/profile/capabilities-section';
import ChangePasswordModal from '@/components/profile/change-password-modal';
import SignedInDevices from '@/components/profile/signed-in-devices';
import { ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { IconButton } from '@/components/ui/icon-button';
import { useDarkMode } from '@/hooks/use-dark-mode';
import { api, apiUrl } from '@/lib/api';

const deviceTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ''; }
};

// Curated, "collapsed" set of common time zones (one representative per region) with friendly
// names. The full IANA list is ~400 near-duplicate entries; this keeps the picker usable. Each is
// shown with its live UTC offset, computed below. A stored zone outside this list stays selectable.
const TZ_ZONES: { id: string; label: string }[] = [
  { id: 'Pacific/Pago_Pago', label: 'Samoa / Pago Pago' },
  { id: 'Pacific/Honolulu', label: 'Hawaii' },
  { id: 'America/Anchorage', label: 'Alaska' },
  { id: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
  { id: 'America/Phoenix', label: 'Arizona (no DST)' },
  { id: 'America/Denver', label: 'Mountain Time (US & Canada)' },
  { id: 'America/Chicago', label: 'Central Time (US & Canada)' },
  { id: 'America/Mexico_City', label: 'Mexico City' },
  { id: 'America/New_York', label: 'Eastern Time (US & Canada)' },
  { id: 'America/Bogota', label: 'Colombia / Peru / Ecuador' },
  { id: 'America/Halifax', label: 'Atlantic Time (Canada)' },
  { id: 'America/Puerto_Rico', label: 'Puerto Rico / US Virgin Islands' },
  { id: 'America/Santiago', label: 'Santiago' },
  { id: 'America/St_Johns', label: 'Newfoundland' },
  { id: 'America/Sao_Paulo', label: 'São Paulo / Brasília' },
  { id: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires / Montevideo' },
  { id: 'Atlantic/Cape_Verde', label: 'Cape Verde' },
  { id: 'Atlantic/Azores', label: 'Azores' },
  { id: 'UTC', label: 'UTC' },
  { id: 'Europe/London', label: 'London / Dublin / Lisbon' },
  { id: 'Africa/Lagos', label: 'West Africa (Lagos)' },
  { id: 'Europe/Paris', label: 'Central Europe (Paris, Berlin, Madrid, Rome)' },
  { id: 'Europe/Athens', label: 'Eastern Europe (Athens, Helsinki)' },
  { id: 'Europe/Kyiv', label: 'Kyiv' },
  { id: 'Africa/Cairo', label: 'Cairo' },
  { id: 'Africa/Johannesburg', label: 'Johannesburg' },
  { id: 'Africa/Nairobi', label: 'East Africa (Nairobi)' },
  { id: 'Asia/Jerusalem', label: 'Jerusalem' },
  { id: 'Europe/Moscow', label: 'Moscow' },
  { id: 'Europe/Istanbul', label: 'Istanbul' },
  { id: 'Asia/Tehran', label: 'Iran (Tehran)' },
  { id: 'Asia/Dubai', label: 'Dubai / Abu Dhabi' },
  { id: 'Asia/Kabul', label: 'Afghanistan (Kabul)' },
  { id: 'Asia/Karachi', label: 'Pakistan (Karachi)' },
  { id: 'Asia/Kolkata', label: 'India (Kolkata, Mumbai)' },
  { id: 'Asia/Kathmandu', label: 'Nepal (Kathmandu)' },
  { id: 'Asia/Dhaka', label: 'Bangladesh (Dhaka)' },
  { id: 'Asia/Yangon', label: 'Myanmar (Yangon)' },
  { id: 'Asia/Bangkok', label: 'Bangkok / Jakarta / Hanoi' },
  { id: 'Asia/Shanghai', label: 'China (Beijing, Shanghai, Hong Kong)' },
  { id: 'Asia/Singapore', label: 'Singapore / Kuala Lumpur' },
  { id: 'Australia/Perth', label: 'Perth' },
  { id: 'Asia/Tokyo', label: 'Japan (Tokyo)' },
  { id: 'Asia/Seoul', label: 'Korea (Seoul)' },
  { id: 'Australia/Darwin', label: 'Darwin (no DST)' },
  { id: 'Australia/Adelaide', label: 'Adelaide' },
  { id: 'Australia/Brisbane', label: 'Brisbane (no DST)' },
  { id: 'Australia/Sydney', label: 'Sydney / Melbourne' },
  { id: 'Pacific/Guadalcanal', label: 'Solomon Is. / New Caledonia' },
  { id: 'Pacific/Auckland', label: 'New Zealand (Auckland)' },
  { id: 'Pacific/Fiji', label: 'Fiji' },
  { id: 'Pacific/Tongatapu', label: 'Tonga' },
];

function tzOffsetMinutes(tz: string): number {
  try {
    const name = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' })
      .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value ?? '';
    const m = name.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    return m ? (m[1] === '-' ? -1 : 1) * (parseInt(m[2] ?? '0', 10) * 60 + parseInt(m[3] ?? '0', 10)) : 0;
  } catch { return 0; }
}
function gmt(min: number): string {
  const s = min < 0 ? '-' : '+';
  const a = Math.abs(min);
  return `GMT${s}${String(Math.floor(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}`;
}
function tzLabel(tz: string): string {
  const z = TZ_ZONES.find((x) => x.id === tz);
  return `(${gmt(tzOffsetMinutes(tz))}) ${z?.label ?? tz}`;
}
const TZ_OPTIONS = [...TZ_ZONES]
  .sort((a, b) => tzOffsetMinutes(a.id) - tzOffsetMinutes(b.id))
  .map((z) => ({ value: z.id, display: tzLabel(z.id) }));
const tzOptionsFor = (value: string) =>
  value && !TZ_OPTIONS.some((o) => o.value === value) ? [{ value, display: tzLabel(value) }, ...TZ_OPTIONS] : TZ_OPTIONS;

const inputCls = 'h-10 w-full rounded-lg border border-ws-border bg-ws-control px-3 text-sm text-ws-text placeholder:text-ws-muted disabled:opacity-60';
const labelCls = 'text-sm font-medium text-ws-text';
const outlineBtn = 'rounded-lg border border-ws-border bg-ws-btn px-4 py-2 text-sm font-medium text-ws-text hover:bg-ws-hover';
const THEMES = ['light', 'dark', 'system'] as const;

// Prominent section header — the app's teal panel-banner treatment.
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className='overflow-hidden rounded-xl border border-ws-border bg-ws-surface'>
      <h2 className='border-b border-ws-primary/20 bg-ws-primary-subtle px-5 py-2.5 text-base font-semibold text-ws-primary dark:bg-ws-text-link/20 dark:text-ws-text-link'>{title}</h2>
      <div className='space-y-5 p-6'>{children}</div>
    </section>
  );
}

export default function ProfileSettingsPage({ view }: { view: 'account' | 'settings' }) {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useDarkMode();
  const themeRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const [profile, setProfile] = useState<Profile | null>(null);
  const [fullName, setFullName] = useState('');
  const [homeLocation, setHomeLocation] = useState('');
  const [homeTimezone, setHomeTimezone] = useState('');
  const [currentLocation, setCurrentLocation] = useState('');
  const [currentTimezone, setCurrentTimezone] = useState('');
  const [autoTimezone, setAutoTimezone] = useState(false);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingAffiliation, setEditingAffiliation] = useState<Affiliation | null>(null);
  const [armedAffId, setArmedAffId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showAvatar, setShowAvatar] = useState(false);
  const [removal, setRemoval] = useState<{ affiliation: Affiliation; usage: AffiliationUsage[] } | null>(null);

  useEffect(() => {
    api.get<Profile>('/profile').then((p) => {
      setProfile(p);
      setFullName(p.fullName);
      setHomeLocation(p.homeLocation ?? '');
      setHomeTimezone(p.homeTimezone ?? '');
      setCurrentLocation(p.currentLocation ?? '');
      setAutoTimezone(!!p.autoTimezone);
      const device = deviceTimezone();
      if (p.autoTimezone && device && device !== p.currentTimezone) {
        setCurrentTimezone(device);
        api.patch<Profile>('/profile', { currentTimezone: device }).then(setProfile).catch(() => {});
      } else {
        setCurrentTimezone(p.currentTimezone ?? '');
      }
    });
  }, []);

  // Everything autosaves (on blur for text, on change for selects/toggles), matching collections +
  // affiliations. No success flash for now — the editor's autosave messaging will be ported here.
  async function patchProfile(body: Record<string, unknown>) {
    setError('');
    try {
      setProfile(await api.patch<Profile>('/profile', body));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  }

  const activeAffiliations = (profile?.affiliations ?? []).filter((a) => a.active !== false);

  async function persistAffiliations(affiliations: Affiliation[]) {
    try {
      setProfile(await api.patch<Profile>('/profile', { affiliations }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save affiliation');
    }
  }

  async function handleSaveAffiliation(aff: Affiliation) {
    if (!profile) return;
    const affiliations = editingAffiliation
      ? profile.affiliations.map((a) => (a.id === aff.id ? { ...a, ...aff } : a))
      : [...profile.affiliations, aff];
    await persistAffiliations(affiliations);
    setModalOpen(false);
  }

  function setPrimary(id: string) {
    if (!profile) return;
    // Toggle: clicking the current primary clears it (no primary is valid — e.g. an adjunct).
    const wasPrimary = profile.affiliations.find((a) => a.id === id)?.primary;
    void persistAffiliations(profile.affiliations.map((a) => ({ ...a, primary: wasPrimary ? false : a.id === id })));
  }

  async function requestDeleteAffiliation(id: string) {
    const aff = (profile?.affiliations ?? []).find((a) => a.id === id);
    if (!aff) return;
    try {
      const usage = await api.get<AffiliationUsage[]>(`/collections/affiliations/${id}/usage`);
      if (usage.length === 0) setProfile(await api.delete<Profile>(`/profile/affiliations/${id}`));
      else setRemoval({ affiliation: aff, usage });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove affiliation');
    }
  }

  async function confirmRemoval(toAffiliationId: string | null) {
    if (!removal) return;
    const id = removal.affiliation.id;
    setRemoval(null);
    try {
      await api.post('/collections/reassign-affiliation', { fromAffiliationId: id, toAffiliationId: toAffiliationId ?? undefined });
      setProfile(await api.delete<Profile>(`/profile/affiliations/${id}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove affiliation');
    }
  }

  function toggleAuto(next: boolean) {
    setAutoTimezone(next);
    const device = deviceTimezone();
    if (next && device) setCurrentTimezone(device);
    void patchProfile({ autoTimezone: next, ...(next && device ? { currentTimezone: device } : {}) });
  }

  if (!profile) {
    return (
      <div className='flex h-screen flex-col overflow-hidden bg-ws-bg'>
        <DashboardHeader />
        <div className='flex flex-1 items-center justify-center text-sm text-ws-muted'>{t('profile.loading')}</div>
      </div>
    );
  }

  return (
    <div className='flex h-screen flex-col overflow-hidden bg-ws-bg'>
      <DashboardHeader />
      <div className='flex-1 overflow-y-auto'>
        <div className='mx-auto max-w-2xl px-4 py-6 sm:px-6'>
          <h1 className='mb-6 text-2xl font-bold text-ws-text'>{t(view === 'account' ? 'profile.tabs.account' : 'profile.tabs.settings')}</h1>
          {error && <p role='alert' className='mb-4 text-sm text-ws-error-fg'>{error}</p>}

          {view === 'account' ? (
            <div className='space-y-6'>
              <Section title={t('profile.userInfo')}>
                <div className='flex justify-center'>
                  <div className='relative inline-block'>
                    <img src={profile.avatarUrl ? apiUrl(profile.avatarUrl) : `${import.meta.env.BASE_URL}images/avatars/${profile.avatar}.png`} alt={profile.avatar} className='h-24 w-24 rounded-full object-cover' />
                    <button
                      onClick={() => setShowAvatar(true)}
                      className='absolute bottom-0 right-0 flex h-9 w-9 items-center justify-center rounded-xl border border-ws-border bg-ws-surface text-ws-muted shadow-sm hover:bg-ws-hover'
                      aria-label={t('profile.editAvatar')}
                    >
                      <Pencil className='h-4 w-4' />
                    </button>
                  </div>
                </div>
                <div className='space-y-1.5'>
                  <label htmlFor='pf-username' className={labelCls}>{t('profile.username')}</label>
                  <input id='pf-username' value={profile.username} disabled className={inputCls} />
                </div>
                <div className='space-y-1.5'>
                  <label htmlFor='pf-email' className={labelCls}>{t('profile.accountEmail')}</label>
                  <input id='pf-email' value={profile.email} disabled className={inputCls} />
                </div>
                <div className='space-y-1.5'>
                  <label htmlFor='pf-fullname' className={labelCls}>{t('profile.fullName')}</label>
                  <input
                    id='pf-fullname'
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    onBlur={() => { if (fullName.trim().length >= 3 && fullName.trim() !== profile.fullName) void patchProfile({ fullName: fullName.trim() }); }}
                    placeholder={t('profile.fullNamePlaceholder')}
                    className={inputCls}
                  />
                </div>
                <div className='space-y-1.5'>
                  <label htmlFor='pf-password' className={labelCls}>{t('profile.password')}</label>
                  <div className='flex items-center gap-2'>
                    <input id='pf-password' type='text' value='**********' disabled className={`${inputCls} flex-1 tracking-widest`} />
                    <button onClick={() => setShowPassword(true)} className={outlineBtn}>{t('common.change')}</button>
                  </div>
                </div>
              </Section>

              <Section title={t('profile.affiliations')}>
                <div className='grid gap-3 sm:grid-cols-2'>
                  {activeAffiliations.map((aff) => {
                    const isArmed = armedAffId === aff.id;
                    return (
                      <div
                        key={aff.id}
                        className={`relative space-y-1 rounded-lg border bg-ws-bg p-4 transition ${
                          isArmed
                            ? `border-ws-warning-fg ${ARMED_DELETE_PAINT}`
                            : 'border-ws-border hover:border-ws-primary'
                        }`}
                      >
                        <div className='flex items-start justify-between gap-2'>
                          <div className='min-w-0'>
                            {/* Stretched `::after` makes the whole card an edit target for mouse, while
                                the button itself is the keyboard/AT-accessible control. */}
                            <button
                              type='button'
                              onClick={() => { setEditingAffiliation(aff); setModalOpen(true); }}
                              aria-label={t('profile.editAffiliationNamed', { institution: aff.institution })}
                              className='block w-full cursor-pointer truncate text-left text-sm font-semibold text-ws-text after:absolute after:inset-0 after:rounded-lg hover:text-ws-primary focus-visible:underline focus:outline-none'
                            >
                              {aff.institution}
                            </button>
                            {aff.primary && <span className='text-xs font-medium text-ws-primary'>{t('profile.primaryTag')}</span>}
                          </div>
                          <div className='relative z-10 flex shrink-0 items-center gap-0.5'>
                            <button
                              onClick={() => setPrimary(aff.id)}
                              aria-label={aff.primary ? t('profile.unsetPrimary') : t('profile.setPrimary')}
                              title={aff.primary ? t('profile.unsetPrimary') : t('profile.setPrimary')}
                              className='flex h-7 w-7 items-center justify-center rounded-md text-ws-muted hover:bg-ws-hover'
                            >
                              <Star className={`h-3.5 w-3.5 ${aff.primary ? 'fill-ws-primary text-ws-primary' : ''}`} />
                            </button>
                            <IconButton
                              variant='destructive'
                              size='sm'
                              isArmed={isArmed}
                              aria-label={isArmed ? t('common.confirmDelete') : t('common.delete')}
                              onClick={() => { if (isArmed) { setArmedAffId(null); void requestDeleteAffiliation(aff.id); } else setArmedAffId(aff.id); }}
                              onPointerLeave={(e) => { if (isArmed && e.pointerType !== 'touch') setArmedAffId(null); }}
                              onBlur={() => { if (isArmed) setArmedAffId(null); }}
                            >
                              <Trash2 className='h-3.5 w-3.5' />
                            </IconButton>
                          </div>
                        </div>
                        <p className='text-xs text-ws-muted'>{aff.location}</p>
                        <div className='space-y-0.5 pt-1'>
                          {aff.emails.map((e, i) => <p key={i} className='truncate text-xs text-ws-text-link'>{e.value}</p>)}
                        </div>
                      </div>
                    );
                  })}
                  <button
                    onClick={() => { setEditingAffiliation(null); setModalOpen(true); }}
                    className='flex min-h-[90px] items-center justify-center gap-2 rounded-lg border border-dashed border-ws-border-strong bg-ws-bg p-4 text-sm text-ws-muted hover:border-ws-primary hover:text-ws-primary'
                  >
                    <Plus className='h-4 w-4' />{t('profile.addAffiliation')}
                  </button>
                </div>
              </Section>

              <Section title={t('profile.devices.title')}>
                <SignedInDevices />
              </Section>
            </div>
          ) : (
            <div className='space-y-6'>
              <Section title={t('profile.settings.appearance')}>
                <div role='radiogroup' aria-label={t('profile.settings.appearance')} className='inline-flex w-fit rounded-lg border border-ws-border p-1'>
                  {THEMES.map((opt, i) => (
                    <button
                      key={opt}
                      ref={(el) => { themeRefs.current[i] = el; }}
                      type='button'
                      role='radio'
                      aria-checked={theme === opt}
                      tabIndex={theme === opt ? 0 : -1}
                      onClick={() => setTheme(opt)}
                      onKeyDown={(e) => {
                        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
                        e.preventDefault();
                        const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + THEMES.length) % THEMES.length;
                        setTheme(THEMES[next]!);
                        themeRefs.current[next]?.focus();
                      }}
                      className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${theme === opt ? 'bg-ws-btn-active text-ws-btn-active-fg' : 'text-ws-muted hover:text-ws-text'}`}
                    >
                      {t(`profile.settings.theme.${opt}`)}
                    </button>
                  ))}
                </div>
              </Section>

              <Section title={t('profile.settings.language')}>
                <select
                  aria-label={t('profile.settings.language')}
                  value={i18n.language === 'pt-BR' ? 'pt-BR' : 'en'}
                  onChange={(e) => i18n.changeLanguage(e.target.value)}
                  className={`${inputCls} sm:w-72`}
                >
                  <option value='en'>English (US)</option>
                  <option value='pt-BR'>Português (BR)</option>
                </select>
              </Section>

              <Section title={t('profile.settings.location')}>
                <div className='grid gap-3 sm:grid-cols-2'>
                  <div className='space-y-1.5'>
                    <label htmlFor='pf-home-location' className={labelCls}>{t('profile.settings.homeLocation')}</label>
                    <input
                      id='pf-home-location'
                      value={homeLocation}
                      onChange={(e) => setHomeLocation(e.target.value)}
                      onBlur={() => { if (homeLocation !== (profile.homeLocation ?? '')) void patchProfile({ homeLocation }); }}
                      placeholder={t('profile.settings.homePlaceholder')}
                      className={inputCls}
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <label htmlFor='pf-home-tz' className={labelCls}>{t('profile.settings.homeTimezone')}</label>
                    <select
                      id='pf-home-tz'
                      value={homeTimezone}
                      onChange={(e) => { setHomeTimezone(e.target.value); void patchProfile({ homeTimezone: e.target.value }); }}
                      className={inputCls}
                    >
                      <option value=''>{t('profile.settings.selectTimezone')}</option>
                      {tzOptionsFor(homeTimezone).map((o) => <option key={o.value} value={o.value}>{o.display}</option>)}
                    </select>
                  </div>
                  <div className='space-y-1.5'>
                    <label htmlFor='pf-current-location' className={labelCls}>{t('profile.settings.currentLocation')}</label>
                    <input
                      id='pf-current-location'
                      value={currentLocation}
                      onChange={(e) => setCurrentLocation(e.target.value)}
                      onBlur={() => { if (currentLocation !== (profile.currentLocation ?? '')) void patchProfile({ currentLocation }); }}
                      placeholder={t('profile.settings.currentPlaceholder')}
                      className={inputCls}
                    />
                  </div>
                  <div className='space-y-1.5'>
                    <label htmlFor='pf-current-tz' className={labelCls}>{t('profile.settings.currentTimezone')}</label>
                    <select
                      id='pf-current-tz'
                      value={currentTimezone}
                      onChange={(e) => { setCurrentTimezone(e.target.value); void patchProfile({ currentTimezone: e.target.value }); }}
                      disabled={autoTimezone}
                      className={inputCls}
                    >
                      <option value=''>{t('profile.settings.selectTimezone')}</option>
                      {tzOptionsFor(currentTimezone).map((o) => <option key={o.value} value={o.value}>{o.display}</option>)}
                    </select>
                  </div>
                </div>
                <div className='flex flex-wrap items-center gap-4'>
                  <button
                    type='button'
                    onClick={() => { const tz = deviceTimezone(); setCurrentTimezone(tz); void patchProfile({ currentTimezone: tz }); }}
                    disabled={autoTimezone}
                    className={`${outlineBtn} flex items-center gap-1.5 disabled:opacity-50`}
                  >
                    <LocateFixed className='h-4 w-4' />{t('profile.settings.detectTimezone')}
                  </button>
                  <label className='flex items-center gap-2 text-sm text-ws-text'>
                    <input type='checkbox' checked={autoTimezone} onChange={(e) => toggleAuto(e.target.checked)} className='accent-ws-primary' />
                    {t('profile.settings.keepUpdated')}
                  </label>
                </div>
              </Section>

              <Section title={t('profile.settings.capabilities')}>
                <CapabilitiesSection />
              </Section>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <AffiliationModal
          initial={editingAffiliation}
          onSave={handleSaveAffiliation}
          onClose={() => setModalOpen(false)}
        />
      )}
      {showPassword && <ChangePasswordModal onClose={() => setShowPassword(false)} />}
      {showAvatar && <AvatarModal current={profile.avatar} onSaved={setProfile} onClose={() => setShowAvatar(false)} />}
      {removal && (
        <AffiliationRemovalDialog
          affiliation={removal.affiliation}
          usage={removal.usage}
          others={activeAffiliations.filter((a) => a.id !== removal.affiliation.id)}
          onCancel={() => setRemoval(null)}
          onConfirm={confirmRemoval}
        />
      )}
    </div>
  );
}
